import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { IconChevronDown } from '@tabler/icons-react';
import { WP_ICON_NAV } from '../icons/constants.js';
import { cx } from '../utils/cx.js';
import { isTopOverlay, popOverlay, pushOverlay } from '../utils/overlayEscStack.js';
import styles from './NavCluster.module.css';
import { Pressable } from '../primitives/Pressable.js';
import { ProximityScope } from '../primitives/ProximityHighlight.js';

export type NavClusterItem = {
  to: string;
  label: string;
  icon?: ReactNode;
  count?: number;
  /** Parcours force-click anchor, e.g. `nav-catalog` → `data-tour="nav-catalog"`. */
  tourAnchor?: string;
};

export type NavClusterIconLinkProps = {
  to: string;
  /** Shown as `aria-label` + `title` (icon-only). */
  label: string;
  icon: ReactNode;
  LinkComponent: ElementType<{
    to: string;
    className?: string;
    children?: ReactNode;
    'aria-label'?: string;
    title?: string;
    'aria-current'?: 'page' | boolean;
  }>;
  active?: boolean;
  /** Optional count after icon, separated by a subtle rule (e.g. inbox total). */
  count?: number;
};

/** Icon-only nav control; matches `NavCluster` trigger styling (no chevron). */
export function NavClusterIconLink({ to, label, icon, LinkComponent, active = false, count }: NavClusterIconLinkProps) {
  const isActive = active;
  const hasCount = typeof count === 'number';
  const a11yLabel = hasCount ? `${label} — ${count}` : label;
  return (
    <LinkComponent
      to={to}
      className={cx(styles.trigger, styles.triggerIconOnly, hasCount && styles.triggerIconWithCount)}
      data-wp-proximity-nav=""
      aria-label={a11yLabel}
      title={a11yLabel}
      data-active={isActive ? 'true' : 'false'}
      aria-current={isActive ? 'page' : undefined}
    >
      <span className={styles.triggerIcon}>{icon}</span>
      {hasCount ? (
        <>
          <span className={styles.triggerSep} aria-hidden />
          <span className={styles.triggerCount}>{count}</span>
        </>
      ) : null}
    </LinkComponent>
  );
}

export type NavClusterProps = {
  label: string;
  triggerIcon: ReactNode;
  items: NavClusterItem[];
  /** e.g. `Link` from react-router-dom — must accept `to` + `className` + `children`. */
  LinkComponent: ElementType<{
    to: string;
    className?: string;
    children?: ReactNode;
    onClick?: () => void;
    role?: string;
    'data-tour'?: string;
    'data-wp-proximity-item'?: string;
    'data-active'?: string;
  }>;
  /** True when current route matches any `items[].to`. */
  active?: boolean;
  /** Per-item active predicate (e.g. pathname match). */
  isItemActive?: (to: string) => boolean;
  /** Hover tooltip on trigger (defaults to label + list of destinations). */
  menuTitle?: string;
  /** Parcours force-click anchor on the cluster trigger (opens the flyout). */
  tourAnchor?: string;
};

const OPEN_MS = 80;
const CLOSE_MS = 220;
const PANEL_MIN_WIDTH = 220;

export function NavCluster({
  label,
  triggerIcon,
  items,
  LinkComponent,
  active = false,
  isItemActive,
  menuTitle,
  tourAnchor,
}: NavClusterProps) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const openT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const [portalPos, setPortalPos] = useState<{ top: number; left: number; minWidth: number } | null>(
    null,
  );

  const clearTimers = useCallback(() => {
    if (openT.current) clearTimeout(openT.current);
    if (closeT.current) clearTimeout(closeT.current);
    openT.current = null;
    closeT.current = null;
  }, []);

  const scheduleOpen = useCallback(() => {
    clearTimers();
    openT.current = setTimeout(() => setOpen(true), OPEN_MS);
  }, [clearTimers]);

  const scheduleClose = useCallback(() => {
    clearTimers();
    closeT.current = setTimeout(() => {
      setPinned(false);
      setOpen(false);
    }, CLOSE_MS);
  }, [clearTimers]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const closeMenu = useCallback(() => {
    clearTimers();
    setPinned(false);
    setOpen(false);
  }, [clearTimers]);

  const pinOpen = useCallback(() => {
    clearTimers();
    setPinned(true);
    setOpen(true);
  }, [clearTimers]);

  const togglePinnedMenu = useCallback(() => {
    clearTimers();
    if (open && pinned) {
      closeMenu();
      return;
    }
    setPinned(true);
    setOpen(true);
  }, [clearTimers, closeMenu, open, pinned]);

  const updatePortalPos = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const minWidth = Math.max(PANEL_MIN_WIDTH, r.width);
    let left = r.left;
    left = Math.max(8, Math.min(left, window.innerWidth - minWidth - 8));
    const top = r.bottom + 6;
    setPortalPos({ top, left, minWidth });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPortalPos(null);
      return;
    }
    updatePortalPos();
  }, [open, updatePortalPos]);

  useEffect(() => {
    if (!open) return;
    const onMove = () => updatePortalPos();
    window.addEventListener('resize', onMove);
    document.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      document.removeEventListener('scroll', onMove, true);
    };
  }, [open, updatePortalPos]);

  useEffect(() => {
    if (!tourAnchor) return;
    const onTourOpen = (e: Event) => {
      const detail = (e as CustomEvent<{ tourAnchor?: string }>).detail;
      if (detail?.tourAnchor && detail.tourAnchor !== tourAnchor) return;
      pinOpen();
    };
    window.addEventListener('wp-tour:open-nav-cluster', onTourOpen);
    return () => window.removeEventListener('wp-tour:open-nav-cluster', onTourOpen);
  }, [pinOpen, tourAnchor]);

  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: PointerEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      /* Parcours tip / mask — do not dismiss flyout mid-step. */
      if (t instanceof Element && t.closest('[data-wp-tour-root]')) return;
      closeMenu();
    };
    document.addEventListener('pointerdown', onDocPointer, true);
    return () => document.removeEventListener('pointerdown', onDocPointer, true);
  }, [closeMenu, open]);

  useEffect(() => {
    if (!open) return;
    const stackId = pushOverlay();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !isTopOverlay(stackId)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      closeMenu();
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      popOverlay(stackId);
    };
  }, [closeMenu, open]);

  if (items.length === 0) return null;

  const defaultMenuTitle =
    menuTitle ?? `${label} — ${items.map((it) => it.label).join(' · ')}`;

  if (items.length === 1) {
    const only = items[0];
    const isActive = isItemActive?.(only.to) ?? active;
    return (
      <div className={styles.wrap}>
        <LinkComponent
          to={only.to}
          className={styles.trigger}
          data-wp-proximity-nav=""
          data-active={isActive ? 'true' : 'false'}
          aria-current={isActive ? 'page' : undefined}
          title={defaultMenuTitle}
        >
          <span className={styles.triggerIcon}>{triggerIcon}</span>
          <span className={styles.triggerLabel}>{label}</span>
        </LinkComponent>
      </div>
    );
  }

  const ni = WP_ICON_NAV;
  const panelStyle: CSSProperties | undefined = portalPos
    ? { top: portalPos.top, left: portalPos.left, minWidth: portalPos.minWidth }
    : undefined;

  const panelNode =
    open && portalPos
      ? createPortal(
          <ProximityScope
            axis="y"
            as="ul"
            ref={panelRef}
            className={cx(styles.panel, styles.panelPortal)}
            style={panelStyle}
            role="menu"
            onMouseEnter={pinOpen}
            onMouseLeave={scheduleClose}
          >
            {items.map((it) => {
              const itemActive = isItemActive?.(it.to) ?? false;
              return (
                <li key={it.to} role="none">
                  <LinkComponent
                    to={it.to}
                    className={styles.item}
                    data-wp-proximity-item=""
                    data-tour={it.tourAnchor || undefined}
                    data-active={itemActive ? 'true' : 'false'}
                    role="menuitem"
                    onClick={closeMenu}
                  >
                    {it.icon ? <span className={styles.itemIcon}>{it.icon}</span> : null}
                    <span className={styles.itemLabel}>{it.label}</span>
                    {typeof it.count === 'number' ? (
                      <span className={styles.itemCount}>{it.count}</span>
                    ) : null}
                  </LinkComponent>
                </li>
              );
            })}
          </ProximityScope>,
          document.body,
        )
      : null;

  return (
    <div
      ref={rootRef}
      className={cx(styles.wrap, open && styles.open)}
      data-wp-proximity-nav=""
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
      onFocusCapture={() => {
        scheduleOpen();
      }}
      onBlurCapture={(e) => {
        const t = e.relatedTarget as Node | null;
        if (t && e.currentTarget.contains(t)) return;
        if (t && panelRef.current?.contains(t)) return;
        scheduleClose();
      }}
    >
      <Pressable
        type="button"
        className={styles.trigger}
        data-active={active ? 'true' : 'false'}
        data-tour={tourAnchor || undefined}
        aria-expanded={open}
        aria-haspopup="menu"
        title={defaultMenuTitle}
        onClick={togglePinnedMenu}
      >
        <span className={styles.triggerIcon}>{triggerIcon}</span>
        <span className={styles.triggerLabel}>{label}</span>
        <IconChevronDown {...ni} size={12} className={styles.chev} stroke={1.5} aria-hidden />
      </Pressable>
      {panelNode}
    </div>
  );
}
